import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import LightAdmin from 'components/headers/lightAdmin';
import AnimationRevealPage from 'helpers/AnimationRevealPage';
import { useTranslation } from 'react-i18next';
import Footerr from 'components/footers/Footer';
import NotificationBell from 'components/NotificationBell';
import i18n from 'i18next';

const DashbordAdmin = () => {
        const { t } = useTranslation();
    const [stats, setStats] = useState({
        totalOrders: 0,
        totalRevenue: 0,
        revenueThisMonth: 0,
        revenueThisWeek: 0,
        averageOrderValue: 0,
        totalItemsSold: 0,
        bestSellingProducts: [],
        totalSuppliers: 0,
        totalInfluencers: 0,
        topSupplier: null,
        totalPromoCodeUsage: 0,
        ordersWithPromoCodes: 0,
        topInfluencers: [],
        topPromoCodes: [],
        influencerRevenue: 0,
        totalStock: 0,
        stockSold: 0,
        pendingOrders: 0,
        deliveredOrders: 0,
        cancelledOrders: 0,
        paidOrders: 0,
        pendingPaymentOrders: 0,
    });
    const [isAdmin, setIsAdmin] = useState(false);
    const calculateOrderStats = useCallback((orderList) => {
        const asNumber = (value) => {
            const n = Number(value);
            return Number.isFinite(n) ? n : 0;
        };

        const now = new Date();
        const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        const startOfWeek = new Date(now);
        startOfWeek.setHours(0, 0, 0, 0);
        startOfWeek.setDate(startOfWeek.getDate() - 6);

        let totalRevenue = 0;
        let revenueThisMonth = 0;
        let revenueThisWeek = 0;
        let totalOrders = 0;
        let pendingOrders = 0;
        let deliveredOrders = 0;
        let cancelledOrders = 0;
        let paidOrders = 0;
        let pendingPaymentOrders = 0;
        let totalItemsSold = 0;
        let stockSold = 0;
        let ordersWithPromoCodes = 0;

        const promoCodes = new Set();
        const supplierRevenue = new Map();
        const influencerRevenue = new Map();
        const productSales = new Map();
        const productStock = new Map();
        const uniqueSuppliers = new Set();
        const uniqueInfluencers = new Set();
        const influencerKeyByCustomId = new Map();
        const promoUsage = new Map();

        const unknownProductLabel = t('dashboard.unknown_product');
        const unknownSupplierLabel = t('dashboard.unknown_supplier');

        orderList.forEach((order) => {
            if (!order) {
                return;
            }

            const statusRaw = (order.Status ?? order.status ?? '').toString();
            const statusNormalized = statusRaw
                .normalize('NFD')
                .replace(/[\u0300-\u036f]/g, '')
                .toLowerCase();
            const isCancelled = statusNormalized === 'annule' || statusNormalized === 'cancelled';
            const isPending = statusNormalized === 'en attente' || statusNormalized === 'pending';
            const isDelivered = statusNormalized === 'livre' || statusNormalized === 'delivered';

            const paymentAmount = asNumber(order.payment?.Amount ?? order.total ?? order.amount);
            const createdAt = order.created_at ? new Date(order.created_at) : null;

            totalOrders += 1;
            if (isPending) {
                pendingOrders += 1;
            }
            if (isDelivered) {
                deliveredOrders += 1;
            }
            if (isCancelled) {
                cancelledOrders += 1;
            }

            if (!isCancelled) {
                totalRevenue += paymentAmount;

                if (createdAt) {
                    if (createdAt >= startOfMonth && createdAt <= now) {
                        revenueThisMonth += paymentAmount;
                    }
                    if (createdAt >= startOfWeek && createdAt <= now) {
                        revenueThisWeek += paymentAmount;
                    }
                }
            }

            const paymentStatusRaw = (order.payment?.PaymentStatus ?? order.payment?.status ?? '').toString().toLowerCase();
            if (paymentStatusRaw === 'payé' || paymentStatusRaw === 'paid') {
                paidOrders += 1;
            }
            if (paymentStatusRaw === 'en attente' || paymentStatusRaw === 'pending') {
                pendingPaymentOrders += 1;
            }

            const items = Array.isArray(order.order_items) ? order.order_items : [];
            items.forEach((item) => {
                const quantity = asNumber(item.Quantity ?? item.quantity ?? item.Qty ?? 0);
                totalItemsSold += quantity;
                stockSold += quantity;

                const product = item.product ?? {};
                const productId = product.ProductID ?? product.id ?? product.product_id ?? item.ProductID ?? item.product_id ?? product.idProduct ?? product.ID;
                const productName =
                    product.Name?.[i18n.language] ||
                    product.Name?.fr ||
                    product.Name?.ar ||
                    product.Name ||
                    item.product_name ||
                    unknownProductLabel;

                if (quantity > 0) {
                    const salesKey = productId ?? productName;
                    const current = productSales.get(salesKey) || { name: productName, quantity: 0 };
                    current.quantity += quantity;
                    current.name = productName;
                    productSales.set(salesKey, current);
                }

                const stockValue = asNumber(
                    product.Stock ??
                    product.stock ??
                    product.StockQuantity ??
                    product.quantity ??
                    product.total_stock
                );
                if (stockValue > 0) {
                    const stockKey = productId ?? productName;
                    if (!productStock.has(stockKey)) {
                        productStock.set(stockKey, stockValue);
                    }
                }
            });

            const influencersForOrder = new Set();
            const registerInfluencer = (key, name, customId) => {
                const baseKey = key ? String(key).trim() : '';
                if (!baseKey) {
                    return;
                }

                const normalizedCustomId = customId ? String(customId).trim().toUpperCase() : null;
                let canonicalKey = baseKey;

                if (normalizedCustomId) {
                    const knownKey = influencerKeyByCustomId.get(normalizedCustomId);
                    if (knownKey) {
                        canonicalKey = knownKey;
                    } else {
                        canonicalKey = `id:${normalizedCustomId}`;
                        influencerKeyByCustomId.set(normalizedCustomId, canonicalKey);
                    }
                }

                if (influencersForOrder.has(canonicalKey)) {
                    return;
                }
                influencersForOrder.add(canonicalKey);

                const safeName = typeof name === 'string' ? name.trim() : name;
                const fallbackName = safeName || (customId ? String(customId).trim() : baseKey);
                const entry = influencerRevenue.get(canonicalKey) || {
                    name: fallbackName,
                    revenue: 0,
                    orders: 0,
                    customId: customId || null,
                };

                entry.revenue += paymentAmount;
                entry.orders += 1;
                if (safeName) {
                    entry.name = safeName;
                }
                if (customId) {
                    entry.customId = customId;
                }

                influencerRevenue.set(canonicalKey, entry);
                uniqueInfluencers.add(canonicalKey);
            };

            const supplier = order.supplier;
            if (supplier) {
                const supplierKey = supplier.id ?? supplier.SupplierID ?? supplier.custom_id ?? supplier.email ?? supplier.name ?? `supplier-${totalOrders}`;
                uniqueSuppliers.add(supplierKey);
                const supplierName = supplier.name || supplier.custom_id || supplier.email || unknownSupplierLabel;
                const supplierEntry = supplierRevenue.get(supplierKey) || {
                    name: supplierName,
                    revenue: 0,
                    orders: 0,
                    customId: supplier.custom_id || null,
                };
                supplierEntry.revenue += paymentAmount;
                supplierEntry.orders += 1;
                supplierEntry.name = supplierName;
                supplierEntry.customId = supplier.custom_id || supplierEntry.customId;
                supplierRevenue.set(supplierKey, supplierEntry);

                const supplierRole = (supplier.role || supplier.Role || '').toLowerCase();
                if (supplierRole.includes('influ')) {
                    registerInfluencer(`supplier-${supplierKey}`, supplierName, supplier.custom_id);
                }
            }

            const promoCode =
                order.promo_code ||
                order.promoCode ||
                order.promo ||
                order.promo_used ||
                order.customer_info?.promo_code ||
                order.customer_info?.PromoCode ||
                order.customer_info?.code_promo ||
                order.promotion?.code;
            const normalizedPromo = promoCode ? String(promoCode).trim() : null;
            if (normalizedPromo) {
                const normalizedPromoUpper = normalizedPromo.toUpperCase();
                const promoKey = `promo-${normalizedPromoUpper}`;
                registerInfluencer(promoKey, order.customer_info?.Name || normalizedPromoUpper, normalizedPromoUpper);
                promoCodes.add(normalizedPromoUpper);
                ordersWithPromoCodes += 1;

                const promoEntry = promoUsage.get(normalizedPromoUpper) || {
                    code: normalizedPromoUpper,
                    revenue: 0,
                    orders: 0,
                };
                promoEntry.orders += 1;
                promoEntry.revenue += paymentAmount;
                promoUsage.set(normalizedPromoUpper, promoEntry);
            }

            const customerRole = (order.customer_info?.role || '').toLowerCase();
            const customerCustomId = order.customer_info?.custom_id || order.customer_info?.CustomID;
            if (!normalizedPromo && customerCustomId) {
                registerInfluencer(
                    `customer-code-${customerCustomId}`,
                    order.customer_info?.Name,
                    customerCustomId
                );
            } else if (!normalizedPromo && customerRole.includes('influ')) {
                registerInfluencer(
                    `customer-${order.customer_info?.CustomerID ?? order.customer_info?.id ?? order.customer_info?.email ?? customerCustomId ?? totalOrders}`,
                    order.customer_info?.Name,
                    customerCustomId
                );
            }
        });

        const bestSellingProducts = Array.from(productSales.values())
            .sort((a, b) => b.quantity - a.quantity)
            .slice(0, 5);

        const totalStock = Array.from(productStock.values()).reduce((acc, value) => acc + value, 0);

        const supplierRanking = Array.from(supplierRevenue.values()).sort((a, b) => b.revenue - a.revenue);
        const topSupplier = supplierRanking.length ? supplierRanking[0] : null;

        const topInfluencers = Array.from(influencerRevenue.values()).sort((a, b) => b.revenue - a.revenue).slice(0, 5);
        const influencerRevenueTotal = Array.from(influencerRevenue.values()).reduce((acc, entry) => acc + entry.revenue, 0);
        const topPromoCodes = Array.from(promoUsage.values())
            .sort((a, b) => {
                if (b.orders !== a.orders) {
                    return b.orders - a.orders;
                }
                return b.revenue - a.revenue;
            })
            .slice(0, 5);

        setStats({
            totalOrders,
            totalRevenue,
            revenueThisMonth,
            revenueThisWeek,
            averageOrderValue: totalOrders > 0 ? totalRevenue / totalOrders : 0,
            totalItemsSold,
            bestSellingProducts,
            totalSuppliers: uniqueSuppliers.size,
            totalInfluencers: uniqueInfluencers.size,
            topSupplier,
            totalPromoCodeUsage: promoCodes.size,
            ordersWithPromoCodes,
            topInfluencers,
            topPromoCodes,
            influencerRevenue: influencerRevenueTotal,
            totalStock,
            stockSold,
            pendingOrders,
            deliveredOrders,
            cancelledOrders,
            paidOrders,
            pendingPaymentOrders,
        });
    }, [t]);

    const fetchAllOrders = useCallback(async () => {
        try {
            const token = localStorage.getItem('authToken');
            if (!token) {
                return;
            }

            const response = await axios.get('  https://plateformewmg.cloud/api/admin/ordersDashbord', {
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
            });

            if (response.status === 200) {
                console.log('orders', response.data);
                calculateOrderStats(response.data);
            }
        } catch (error) {
            console.error('Error fetching dashboard orders:', error);
        }
    }, [calculateOrderStats]);

    useEffect(() => {
        const adminFlag = localStorage.getItem('isAdmin') === '1';
        setIsAdmin(adminFlag);
        if (!adminFlag) {
            return;
        }
        fetchAllOrders();
    }, [fetchAllOrders]);

    const formatCurrency = useMemo(() => (
        (value) => `${Number(value || 0).toFixed(2)} ${t('currency')}`
    ), [t]);

    const formatInteger = useMemo(() => (
        (value) => new Intl.NumberFormat().format(Math.round(Number(value || 0)))
    ), []);

    if (!isAdmin) {
        return <p className="text-danger text-center">Unauthorized: You do not have admin access.</p>;
    }

    const overviewCards = [
        {
            key: 'totalRevenue',
            title: t('dashboard.total_revenue'),
            value: formatCurrency(stats.totalRevenue),
            background: '#133b8c',
        },
        {
            key: 'revenueThisMonth',
            title: t('dashboard.revenue_this_month'),
            value: formatCurrency(stats.revenueThisMonth),
            background: '#0f2b66',
        },
        {
            key: 'revenueThisWeek',
            title: t('dashboard.revenue_this_week'),
            value: formatCurrency(stats.revenueThisWeek),
            background: '#176b87',
        },
        {
            key: 'averageOrderValue',
            title: t('dashboard.average_order_value'),
            value: formatCurrency(stats.averageOrderValue),
            background: '#2c8f8b',
        },
    ];

    const inventoryCards = [
        {
            key: 'totalItemsSold',
            title: t('dashboard.total_items_sold'),
            value: formatInteger(stats.totalItemsSold),
            background: '#557a46',
        },
        {
            key: 'stockSold',
            title: t('dashboard.stock_sold'),
            value: formatInteger(stats.stockSold),
            background: '#7a9d54',
        },
        {
            key: 'totalStock',
            title: t('dashboard.total_stock'),
            value: formatInteger(stats.totalStock),
            background: '#a8cd89',
        },
    ];

    const partnerCards = [
        {
            key: 'totalSuppliers',
            title: t('dashboard.total_suppliers'),
            value: formatInteger(stats.totalSuppliers),
            background: '#4b5943',
        },
        {
            key: 'totalInfluencers',
            title: t('dashboard.total_influencers'),
            value: formatInteger(stats.totalInfluencers),
            background: '#8f9779',
        },
        {
            key: 'influencerRevenue',
            title: t('dashboard.influencer_revenue'),
            value: formatCurrency(stats.influencerRevenue),
            background: '#5d7052',
        },
    ];

    const promoCard = {
        totalUsage: formatInteger(stats.totalPromoCodeUsage),
        orders: formatInteger(stats.ordersWithPromoCodes),
    };

    const legacyCards = [
        {
            key: 'legacy-total-orders',
            title: t('Total_des_Commandes'),
            value: formatInteger(stats.totalOrders),
            background: '#7AA95C',
        },
        {
            key: 'legacy-paid-orders',
            title: t('Total_des_Commandes_Payees'),
            value: formatInteger(stats.paidOrders),
            background: '#5D7052',
        },
        {
            key: 'legacy-delivered-orders',
            title: t('Commandes_Livrees'),
            value: formatInteger(stats.deliveredOrders),
            background: '#226D68',
        },
        {
            key: 'legacy-cancelled-orders',
            title: t('Commandes_Annulees'),
            value: formatInteger(stats.cancelledOrders),
            background: '#67944C',
        },
        {
            key: 'legacy-pending-orders',
            title: t('Commandes_en_attente_de_Livraison'),
            value: formatInteger(stats.pendingOrders),
            background: '#4B5943',
        },
        {
            key: 'legacy-pending-payment',
            title: t('Commandes_en_attente_de_Paiement'),
            value: formatInteger(stats.pendingPaymentOrders),
            background: '#ACC1AE',
        },
    ];
    return (
        <AnimationRevealPage>
            <LightAdmin />
                              <NotificationBell />

            <div className="d-flex">
                <section className="w-100 p-3 pb-5 mb-5">
                    <div className="d-flex justify-content-start align-items-center gap-2">
                        {/* <h1 className="text-dark display-4">Admin Dashboard</h1> */}
                    </div>
                    <hr className="my-4" />

                    {/* Order Statistics Cards */}
                    <div className="row g-2 mb-2">
                        {legacyCards.map((card) => (
                            <div className="col-lg-4 col-xl-2 col-sm-6" key={card.key}>
                                <div className="card text-white h-100 shadow" style={{ backgroundColor: card.background }}>
                                    <div className="card-body d-flex flex-column justify-content-between">
                                        <h6 className="card-title">{card.title}</h6>
                                        <p className="card-text fs-4 mb-0">{card.value}</p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="row g-2">
                        {overviewCards.map((card) => (
                            <div className="col-md-3 col-sm-6" key={card.key}>
                                <div className="card text-white h-100 shadow" style={{ backgroundColor: card.background }}>
                                    <div className="card-body d-flex flex-column justify-content-between">
                                        <h6 className="card-title">{card.title}</h6>
                                        <p className="card-text fs-4 mb-0">{card.value}</p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="row g-2 mt-2">
                        {inventoryCards.map((card) => (
                            <div className="col-md-4 col-sm-6" key={card.key}>
                                <div className="card text-white h-100 shadow" style={{ backgroundColor: card.background }}>
                                    <div className="card-body d-flex flex-column justify-content-between">
                                        <h6 className="card-title">{card.title}</h6>
                                        <p className="card-text fs-4 mb-0">{card.value}</p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="row g-2 mt-2">
                        {partnerCards.map((card) => (
                            <div className="col-md-4 col-sm-6" key={card.key}>
                                <div className="card text-white h-100 shadow" style={{ backgroundColor: card.background }}>
                                    <div className="card-body d-flex flex-column justify-content-between">
                                        <h6 className="card-title">{card.title}</h6>
                                        <p className="card-text fs-4 mb-0">{card.value}</p>
                                    </div>
                                </div>
                            </div>
                        ))}
                        <div className="col-md-4 col-sm-6">
                            <div className="card h-100 shadow" style={{ backgroundColor: '#96b38a', color: '#ffffff' }}>
                                <div className="card-body d-flex flex-column justify-content-between">
                                    <h6 className="card-title text-white">{t('dashboard.total_promo_usage')}</h6>
                                    <div>
                                        <p className="fs-4 mb-1 text-white">{promoCard.totalUsage}</p>
                                        <p className="mb-0" style={{ color: '#e9f3e5' }}>
                                            {t('dashboard.orders_with_promo')}: {promoCard.orders}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="row g-2 mt-2">
                        <div className="col-lg-6">
                            <div className="card h-100 shadow">
                                <div className="card-body">
                                    <h6 className="card-title">{t('dashboard.best_selling_products')}</h6>
                                    <ul className="list-group list-group-flush">
                                        {stats.bestSellingProducts.length ? (
                                            stats.bestSellingProducts.map((product) => (
                                                <li key={`${product.name}-${product.quantity}`} className="list-group-item d-flex justify-content-between align-items-center">
                                                    <span>{product.name}</span>
                                                    <span className="fw-semibold">
                                                        {t('dashboard.items_label', { count: product.quantity })}
                                                    </span>
                                                </li>
                                            ))
                                        ) : (
                                            <li className="list-group-item text-muted">{t('dashboard.no_data')}</li>
                                        )}
                                    </ul>
                                </div>
                            </div>
                        </div>
                        <div className="col-lg-6">
                            <div className="card h-100 shadow mb-3">
                                <div className="card-body">
                                    <h6 className="card-title">{t('dashboard.top_supplier')}</h6>
                                    {stats.topSupplier ? (
                                        <div>
                                            <p className="fs-5 fw-semibold mb-1">{stats.topSupplier.name}</p>
                                            {stats.topSupplier.customId && (
                                                <p className="text-muted mb-1">{stats.topSupplier.customId}</p>
                                            )}
                                            <p className="mb-1">
                                                {t('dashboard.orders_label', { count: stats.topSupplier.orders })}
                                            </p>
                                            <p className="mb-0">{formatCurrency(stats.topSupplier.revenue)}</p>
                                        </div>
                                    ) : (
                                        <p className="mb-0 text-muted">{t('dashboard.no_data')}</p>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="row g-2 mt-2">
                        <div className="col-lg-6">
                            <div className="card h-100 shadow">
                                <div className="card-body">
                                    <h6 className="card-title">{t('dashboard.top_influencers')}</h6>
                                    <ul className="list-group list-group-flush">
                                        {stats.topInfluencers.length ? (
                                            stats.topInfluencers.map((influencer) => (
                                                <li key={`${influencer.name}-${influencer.customId ?? influencer.orders}`} className="list-group-item">
                                                    <div className="d-flex justify-content-between align-items-start">
                                                        <div>
                                                            <p className="fw-semibold mb-0">{influencer.name}</p>
                                                            {influencer.customId && (
                                                                <p className="text-muted mb-1">{influencer.customId}</p>
                                                            )}
                                                            <p className="mb-0 text-muted">
                                                                {t('dashboard.orders_label', { count: influencer.orders })}
                                                            </p>
                                                        </div>
                                                        <span className="fw-semibold">{formatCurrency(influencer.revenue)}</span>
                                                    </div>
                                                </li>
                                            ))
                                        ) : (
                                            <li className="list-group-item text-muted">{t('dashboard.no_data')}</li>
                                        )}
                                    </ul>
                                </div>
                            </div>
                        </div>
                        <div className="col-lg-6">
                            <div className="card h-100 shadow">
                                <div className="card-body">
                                    <h6 className="card-title">{t('dashboard.top_promo_codes', { defaultValue: 'Codes promo les plus utilisés' })}</h6>
                                    <ul className="list-group list-group-flush">
                                        {stats.topPromoCodes.length ? (
                                            stats.topPromoCodes.map((promo) => (
                                                <li key={promo.code} className="list-group-item">
                                                    <div className="d-flex justify-content-between align-items-start">
                                                        <div>
                                                            <p className="fw-semibold mb-0">{promo.code}</p>
                                                            <p className="mb-0 text-muted">
                                                                {t('dashboard.orders_label', { count: promo.orders })}
                                                            </p>
                                                        </div>
                                                        <span className="fw-semibold">{formatCurrency(promo.revenue)}</span>
                                                    </div>
                                                </li>
                                            ))
                                        ) : (
                                            <li className="list-group-item text-muted">{t('dashboard.no_data')}</li>
                                        )}
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </div>

            <Footerr />
        </AnimationRevealPage>
    );
};

export default DashbordAdmin;
